import type {
  IndexStatusSummary,
  ProjectJob,
  ProjectJobState,
} from '../api/types';
import type { CentrifugeProgressMessage } from '@/hooks/useCentrifugeProgress';
import { inspectUrlsByIndexStatus } from './inspect-detail';
import { isJobDone } from './job-labels';

export type JobProgressCompletionMeta = {
  state?: ProjectJobState;
  jobKind?: 'inspect' | 'google_index' | 'indexnow';
  indexStatusSummary?: IndexStatusSummary;
};

export type OnboardingInspectCompletePayload = {
  jobId: string;
  state: ProjectJobState;
  indexStatusSummary: IndexStatusSummary;
  notIndexedUrls: string[];
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asIndexStatusSummary(value: unknown): IndexStatusSummary | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  const indexed = record.indexed;
  const notIndexed = record.notIndexed;
  if (typeof indexed !== 'number' || typeof notIndexed !== 'number') {
    return undefined;
  }
  return { indexed, notIndexed };
}

/** Parse completion fields from Centrifugo job progress payload. */
export function parseJobProgressCompletion(
  raw: CentrifugeProgressMessage | null | undefined,
): JobProgressCompletionMeta | null {
  if (!raw?.finished) return null;

  const state = typeof raw.state === 'string' ? raw.state : undefined;
  const jobKind =
    raw.jobKind === 'inspect' ||
    raw.jobKind === 'google_index' ||
    raw.jobKind === 'indexnow'
      ? raw.jobKind
      : undefined;
  const indexStatusSummary = asIndexStatusSummary(raw.indexStatusSummary);

  if (!state && !jobKind && !indexStatusSummary) {
    return null;
  }

  return {
    state: state as ProjectJobState | undefined,
    jobKind,
    indexStatusSummary,
  };
}

/**
 * @deprecated Do not use for onboarding navigation — always load via REST.
 * Socket summary alone can be 0/0 before results persist.
 */
export function inspectCompleteFromSocketMeta(
  jobId: string,
  meta: JobProgressCompletionMeta,
): OnboardingInspectCompletePayload | null {
  if (!meta.state || !meta.indexStatusSummary || !isJobDone(meta.state)) {
    return null;
  }
  const { indexed, notIndexed } = meta.indexStatusSummary;
  if (notIndexed > 0 || indexed === 0) {
    return null;
  }
  return {
    jobId,
    state: meta.state,
    indexStatusSummary: meta.indexStatusSummary,
    notIndexedUrls: [],
  };
}

/** Load full onboarding inspect payload from REST (URL list lives here, not on socket). */
export function inspectCompleteFromJob(
  job: ProjectJob,
): OnboardingInspectCompletePayload | null {
  if (job.kind !== 'inspect' || !isJobDone(job.state)) return null;
  const results = job.results ?? [];
  if (results.length === 0) return null;

  const notIndexedUrls =
    job.notIndexedUrls ??
    inspectUrlsByIndexStatus(results, 'notIndexed');
  const indexed = inspectUrlsByIndexStatus(results, 'indexed').length;

  return {
    jobId: job.id,
    state: job.state,
    indexStatusSummary: job.indexStatusSummary ?? {
      indexed,
      notIndexed: notIndexedUrls.length,
    },
    notIndexedUrls,
  };
}
